import React, { FC, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { CloseOutlined, FormOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import * as gtag from 'lib/gtag';
import dynamic from 'next/dynamic';

const FormModal = dynamic(() => import('../FormModal'));

// --- ANIMACJE (BEZ ZMIAN) ---
const slideInDesktop = keyframes`
  to {
    transform: translate(0);
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
`;

const slideInMobile = keyframes`
  to {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
`;

interface BubbleProps {
    $isClosed: boolean;
}

// --- STYLED COMPONENTS (LIFTING) ---

const Bubble = styled.div<BubbleProps>`
    position: fixed;
    width: 400px; /* Równe wymiary */
    max-width: 100%;
    height: 140px; /* Nieco zgrabniejszy, mniej "tłusty" */
    right: 0;
    bottom: 15%; /* Złoty środek */

    background: ${({ theme }) => theme.colors.primary};
    /* Dodany cień dla głębi */
    box-shadow: -5px 10px 25px rgba(0, 0, 0, 0.15);
    border-radius: 24px 0 0 24px;
    z-index: 1000;

    /* Animacja */
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translate(403px);

    animation: ${slideInDesktop} linear both;
    animation-timeline: scroll(root);
    animation-range: 600px 750px;

    display: ${(props) => (props.$isClosed ? 'none' : 'flex')};
    align-items: center;
    padding: 0;

    @media (min-width: 992px) and (max-width: 1366px) {
        bottom: 10%;
    }

    @media (max-width: 992px) {
        bottom: 0;
        border-radius: 0;
        height: auto;
        min-height: 100px;
        width: 100%;
        transform: translateY(110px);
        animation-name: ${slideInMobile};
        padding-bottom: env(safe-area-inset-bottom);
    }
`;

const InnerContent = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 0 20px 0 30px;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.95;
    }

    @media (max-width: 992px) {
        padding: 15px;
        justify-content: center;
    }
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    margin-right: 20px;
    flex-shrink: 0;

    @media (max-width: 992px) {
        width: 60px;
        height: 60px;
        margin-right: 15px;
    }
`;

const TextContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    color: #fff;
    text-align: left;
`;

const Title = styled.div`
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.9;
    font-weight: 500;
    margin-bottom: 4px;
`;

const Subtitle = styled.div`
    font-size: 22px;
    line-height: 1.2;
    font-weight: 700;

    span {
        display: block;
        font-weight: 300;
        font-size: 18px;
    }

    @media (max-width: 992px) {
        font-size: 18px;
        span {
            font-size: 16px;
        }
    }
`;

const CloseButton = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    padding: 5px;
    z-index: 2;
    transition: color 0.3s;

    &:hover {
        color: #fff;
    }

    svg {
        font-size: 18px;
    }
`;

const NewsletterBubble: FC = () => {
    const [visible, setVisible] = useState<boolean>(false);
    const [isClosed, setClosed] = useState<boolean>(false);

    useEffect(() => {
        // Sprawdzamy ciasteczko
        const cookieClosed = Cookies.get('newsletter-bubble-closed') === 'true';
        setClosed(cookieClosed);
    }, []);

    const onCancel = () => {
        setVisible(false);
    };

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        Cookies.set('newsletter-bubble-closed', 'true', { expires: 31 });
        setClosed(true);
    };

    const handleShow = () => {
        setVisible(true);
        gtag.event({
            action: 'click',
            category: 'Newsletter',
            label: 'Newsletter',
            value: 'open'
        });
    };

    if (isClosed) return null;

    return (
        <>
            <Bubble $isClosed={isClosed} role="dialog" aria-label="Newsletter Popup">
                <CloseButton onClick={handleClose} aria-label="Zamknij">
                    <CloseOutlined />
                </CloseButton>

                <InnerContent onClick={handleShow}>
                    <IconWrapper>
                        <FormOutlined style={{ fontSize: 36, color: '#fff' }} />
                    </IconWrapper>

                    <TextContent>
                        <Title>Bądź na bieżąco</Title>
                        <Subtitle>
                            <span>Zapisz się na nasz</span>
                            Newsletter!
                        </Subtitle>
                    </TextContent>
                </InnerContent>
            </Bubble>

            {visible && <FormModal visible={visible} handleClose={onCancel} />}
        </>
    );
};

export default NewsletterBubble;
